import { type ReactNode } from 'react';
import { Checkbox, Pagination, Table } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import { Empty } from './Empty';
import { Loading } from './Loading';
import { useBreakpoint } from './useBreakpoint';

export interface ResponsiveListProps<T> {
  dataSource: T[];
  rowKey: (record: T) => string | number;
  loading?: boolean;
  /** Full AntD Table props used on pad / desktop (existing column defs). */
  tableProps: TableProps<T>;
  /** Card renderer for the mobile breakpoint. */
  renderCard: (record: T) => ReactNode;
  pagination?: TablePaginationConfig | false;
  /** Selected keys; when provided, cards show a selection checkbox. */
  selectedRowKeys?: Array<string | number>;
  onSelectChange?: (keys: Array<string | number>) => void;
  emptyText?: ReactNode;
}

export function ResponsiveList<T>({
  dataSource,
  rowKey,
  loading,
  tableProps,
  renderCard,
  pagination,
  selectedRowKeys,
  onSelectChange,
  emptyText = '暂无数据',
}: ResponsiveListProps<T>) {
  const breakpoint = useBreakpoint();

  if (breakpoint !== 'mobile') {
    return (
      <Table<T>
        {...tableProps}
        rowKey={rowKey as TableProps<T>['rowKey']}
        loading={loading}
        dataSource={dataSource}
        pagination={pagination}
      />
    );
  }

  const selectable = selectedRowKeys != null && onSelectChange != null;
  const selectedSet = new Set(selectedRowKeys ?? []);

  const toggle = (key: string | number, checked: boolean) => {
    if (!onSelectChange) return;
    const next = new Set(selectedSet);
    if (checked) next.add(key);
    else next.delete(key);
    onSelectChange(Array.from(next));
  };

  return (
    <div className="mall-card-list">
      {loading ? (
        <Loading />
      ) : dataSource.length === 0 ? (
        <Empty title={emptyText} />
      ) : (
        <>
          {dataSource.map((record) => {
            const key = rowKey(record);
            return (
              <div className="mall-card-list-item" key={key}>
                {selectable && (
                  <Checkbox
                    className="mall-card-list-check"
                    checked={selectedSet.has(key)}
                    onChange={(e) => toggle(key, e.target.checked)}
                  />
                )}
                <div className="mall-card-list-body">{renderCard(record)}</div>
              </div>
            );
          })}
          {pagination && (
            <div className="mall-card-list-pagination">
              <Pagination
                size="small"
                align="center"
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total ?? dataSource.length}
                showSizeChanger={pagination.showSizeChanger}
                pageSizeOptions={pagination.pageSizeOptions as string[] | undefined}
                showTotal={pagination.showTotal}
                onChange={pagination.onChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
